import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AddressDetails,
  AddressSearchResult,
  AddressService,
  AddressType as ApiAddressType,
  AddressValidationResult,
  GeoCoordinate,
} from '@app/api/dmc';
import { AddressType } from '@app/core/types/address-type';

/**
 * Provides address lookups against the Danish Address Register.
 *
 * All calls go through the EnergyConnect backend proxy rather than the address
 * provider directly, so the provider URL and its access token stay server side
 * and the browser is not exposed to third party CORS rules.
 */
@Injectable({
  providedIn: 'root'
})
export class AutocompleteService {
  private readonly addressService = inject(AddressService);

  /**
   * Searches for address suggestions based on partial input.
   *
   * @param addr - The partial address input from the user.
   * @param type The type of address to search for ('address' or 'access'). Defaults to 'address'.
   * @returns An Observable emitting matching suggestions.
   */
  public searchAddress(addr: string, type: AddressType = 'address'): Observable<AddressSearchResult[]> {
    return this.addressService.addressSearchGet(addr, AutocompleteService.toApiType(type));
  }

  public getByAccessAddressId(id: string): Observable<AddressSearchResult[]> {
    return this.addressService.addressAccessAddressesIdAddressesGet(id);
  }

  /**
   * Validates a full address designation against the address register.
   *
   * @param addr - The full address designation to validate.
   * @returns An Observable emitting the validation outcome.
   */
  public validateAddress(addr: string): Observable<AddressValidationResult> {
    return this.addressService.addressValidatePost({ text: addr });
  }

  /**
   * Retrieves detailed address information by its DAR identifier.
   *
   * @param id - The DAR identifier.
   * @param type The type of address to retrieve ('address' or 'access'). Defaults to 'address'.
   * @returns An Observable emitting the address data.
   */
  public getById(id: string, type: AddressType = 'address'): Observable<AddressDetails> {
    return this.addressService.addressIdGet(id, AutocompleteService.toApiType(type));
  }

  /**
   * Retrieves the WGS84 position of an address, used for map display.
   *
   * @param id - The DAR identifier.
   * @returns An Observable emitting the position.
   */
  public getCoordinates(id: string): Observable<GeoCoordinate> {
    return this.addressService.addressIdCoordinatesGet(id);
  }

  private static toApiType(type: AddressType): ApiAddressType {
    return type === 'access' ? ApiAddressType.Access : ApiAddressType.Address;
  }
}
